import React, { useState, useEffect } from 'react'
import {
    Modal,
    Form,
    Button,
    Typography,
    Space,
    message,
    Input
} from 'antd'
import { Order, ChildOrder, RefundSku } from '@a4b/api/src/modules/Monetisation/Puja/types'
import { CopyOutlined } from '@ant-design/icons'

const { Text, Title } = Typography
const { TextArea } = Input

interface RefundModalProps {
    visible: boolean
    onCancel: () => void
    onConfirm: (data: RefundFormData) => void
    order: Order | null
    childOrder?: ChildOrder
    refundType: 'full' | 'partial'
    serviceType: 'pooja' | 'offerings'
    loading?: boolean
}

export interface RefundFormData {
    order_id: string
    child_order_id?: string
    skus: RefundSku[]
    amount: number
    remarks: string
    type: 'full' | 'partial'
    method: 'normal' | 'instant'
    other_charges?: boolean
    is_delivery_charge_available?: boolean
}

const RefundModal: React.FC<RefundModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    order,
    childOrder,
    refundType,
    serviceType,
    loading = false
}) => {
    const [form] = Form.useForm()
    const [refundAmount, setRefundAmount] = useState<number>(0)

    // Get currency symbol based on country code
    const getCurrencySymbol = (countryCode?: string) => {
        return countryCode === 'US' ? '$' : '₹'
    }

    useEffect(() => {
        if (visible && order) {
            // Calculate refund amount based on type
            const amount = childOrder
                ? childOrder.amount
                : (serviceType === 'pooja' ? order.bill?.total_amount : order.total)
            setRefundAmount(amount)

            form.setFieldsValue({
                type: refundType,
                other_charges: refundType === 'full' ? true : false, // Always true for full refund
                remarks: ''
            })
        }
    }, [visible, childOrder, order, refundType, form])

    // Don't render modal if order is null
    if (!order && visible) {
        return null
    }

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()

            // Prepare SKUs based on service type and refund type
            let skus: RefundSku[] = []
            if (serviceType === 'pooja') {
                if (refundType === 'full' && order) {
                    if (order.bill?.items && order.bill.items.length > 0) {
                        // Use bill.items which contains both items and add_ons together
                        skus = order.bill.items.map(item => ({
                            inventory_id: item?.inventory_id?.toString(),
                            amount: item.price
                        }))
                    } else {
                        // Fallback: combine items and add_ons if bill is not available
                        const allItems = []
                        if (order.items && Array.isArray(order.items)) {
                            allItems.push(...order.items)
                        }
                        if (order.add_ons && Array.isArray(order.add_ons)) {
                            allItems.push(...order.add_ons)
                        }
                        skus = allItems.map(item => ({
                            inventory_id: (item.inventory_id || item.id || '').toString(),
                            amount: item.amount || item.price || 0
                        }))
                    }
                }
            } else if (serviceType === 'offerings') {
                skus = order?.skus.map(sku => ({
                    inventory_id: sku.inventory_id.toString(),
                    amount: sku.amount
                })) || []
            }

            const refundData: RefundFormData = {
                order_id: order?.id || '',
                ...(childOrder?.id && { child_order_id: childOrder.id }),
                skus,
                amount: childOrder?.amount || (serviceType === 'pooja' ? order?.bill?.total_amount : order?.total) || 0, // Use child order amount if available
                remarks: values.remarks || '',
                type: 'full',
                method: 'normal',
                other_charges: true
            }
            onConfirm(refundData)
        } catch (error) {
            console.error('Form validation failed:', error)
        }
    }

    const copyOrderId = () => {
        if (order?.id) {
            navigator.clipboard.writeText(order.id)
            message.success('Order ID copied to clipboard')
        }
    }

    return (
        <Modal
            title={null}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered
        >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_6877_25503)">
                        <path d="M20 0C8.95536 0 0 8.95536 0 20C0 31.0446 8.95536 40 20 40C31.0446 40 40 31.0446 40 20C40 8.95536 31.0446 0 20 0ZM20 36.6071C10.8304 36.6071 3.39286 29.1696 3.39286 20C3.39286 10.8304 10.8304 3.39286 20 3.39286C29.1696 3.39286 36.6071 10.8304 36.6071 20C36.6071 29.1696 29.1696 36.6071 20 36.6071Z" fill="#1890FF" />
                        <path d="M17.8564 27.8571C17.8564 28.4255 18.0822 28.9705 18.4841 29.3724C18.8859 29.7742 19.431 30 19.9993 30C20.5676 30 21.1127 29.7742 21.5145 29.3724C21.9164 28.9705 22.1422 28.4255 22.1422 27.8571C22.1422 27.2888 21.9164 26.7438 21.5145 26.3419C21.1127 25.9401 20.5676 25.7143 19.9993 25.7143C19.431 25.7143 18.8859 25.9401 18.4841 26.3419C18.0822 26.7438 17.8564 27.2888 17.8564 27.8571V27.8571ZM18.9279 22.8571H21.0707C21.2672 22.8571 21.4279 22.6964 21.4279 22.5V10.3571C21.4279 10.1607 21.2672 10 21.0707 10H18.9279C18.7314 10 18.5707 10.1607 18.5707 10.3571V22.5C18.5707 22.6964 18.7314 22.8571 18.9279 22.8571Z" fill="#1890FF" />
                    </g>
                    <defs>
                        <clipPath id="clip0_6877_25503">
                            <rect width="40" height="40" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
                <Title level={3} style={{ margin: 0 }}>
                    Are You Sure?
                </Title>
            </div>

            <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 16px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8
                }}>
                    <Text strong>Order ID - {order?.id || 'N/A'}</Text>
                    <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={copyOrderId}
                        size="small"
                        disabled={!order?.id}
                    />
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'left',
                    padding: '12px 16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 8
                }}>
                    <Text>{refundType === 'full' ? 'Full' : 'Partial'} Refund Amount</Text>
                    <Text strong style={{ marginLeft: '10px' }}>{getCurrencySymbol(order?.country_code)}{refundAmount}</Text>
                </div>
            </Space>

            <Form form={form} layout="vertical" style={{ marginBottom: 24 }}>
                <Form.Item
                    label="Remarks"
                    name="remarks"
                    rules={[{ required: true, message: 'Please enter remarks for the refund' }]}
                >
                    <TextArea
                        rows={2}
                        placeholder="Enter remarks for the refund..."
                        style={{ borderRadius: 8 }}
                    />
                </Form.Item>
            </Form>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'right' }}>
                <Button size="large" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit}
                    loading={loading}
                // style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}
                >
                    Proceed with {refundType === 'full' ? 'Full' : 'Partial'} Refund
                </Button>
            </div>
        </Modal>
    )
}

export default RefundModal